import useAnimatedPress from '@ui/animated/useAnimatedPress'
import Icon, { IconName } from '@ui/elements/Icon'
import { Pressable, View } from 'react-native'
import cn from '@ui/utils/cn'
import Highlights, { HighlightsLayers } from '@ui/effects/Highlights'
import Typography from '@ui/typography'
import {
  borderRadius as borderRadiusDefault,
  borderWidthVariant,
  colorsVariant,
  SectionRowVariant,
} from './styles'
import { leadingNone } from '@ui/typography/styles'

interface Props {
  title: string
  required?: boolean
  icon?: IconName
  desc?: string
  info?: string
  variant?: SectionRowVariant
  disabled?: boolean
  onPress?: () => void
  index?: number
  isGrouped?: boolean // used within multiple SectionRows or standalone?
  isContained?: boolean

  iconComponent?: React.ReactNode
  actionComponent?: React.ReactNode
  content?: React.ReactNode
  highlightsLayers?: HighlightsLayers
}

export default function SectionRow({
  title,
  required,
  icon,
  desc,
  info,
  variant = 'default',
  disabled,
  onPress,
  index,
  iconComponent,
  actionComponent,
  content,
  isGrouped = false,
  isContained = false,
  highlightsLayers,
}: Props) {
  const {
    animation: animationPress,
    onPressIn,
    onPressOut,
  } = useAnimatedPress()

  const colors = colorsVariant[variant]
  const hasOuterContainer = isGrouped || isContained

  const borderWidths = {
    borderWidth: hasOuterContainer ? 0 : borderWidthVariant[variant],
  }

  const borderRadiuses = {
    borderRadius: hasOuterContainer ? 0 : borderRadiusDefault,
  }

  return (
    <Pressable
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      onPress={onPress}
      className={cn(
        'relative inset-0',
        disabled && 'cursor-not-allowed',
        content && 'overflow-hidden'
      )}
      style={{ ...borderRadiuses }}
      disabled={disabled}
    >
      <Highlights
        variant={variant}
        borderWidths={borderWidths}
        borderRadiuses={borderRadiuses}
        animationPress={animationPress}
        layers={highlightsLayers}
      />
      <View className="relative inset-0 flex flex-col">
        {isGrouped && index !== 0 && (
          <View
            className="absolute inset-0 w-full bg-black/10"
            style={{ height: 1 }}
          />
        )}
        {isGrouped && !(variant === 'default' || variant === 'disabled') && (
          <View
            className="absolute inset-0 h-full"
            style={{ width: 2, backgroundColor: colors.icon }}
          />
        )}
        <View className="flex min-h-28 flex-row items-center justify-between gap-8 p-8">
          <View className="flex min-w-0 flex-1 flex-row items-center justify-start gap-8">
            {icon && (
              <View className="flex h-8 w-8 items-center justify-center">
                <Icon name={icon} color={colors.icon} />
              </View>
            )}
            {iconComponent && iconComponent}
            <View className="flex min-w-0 flex-1 flex-col items-start justify-start gap-1">
              <View className="flex flex-row items-center justify-start gap-4">
                <View className="flex flex-row items-center justify-start gap-1">
                  <Typography
                    variant="MDB"
                    color={colors.title}
                    numberOfLines={1}
                  >
                    {title}
                  </Typography>
                  {required && (
                    <Typography
                      variant="MDB"
                      className="h-1 w-1 rounded-full"
                      style={[{ color: colors.requiredIndicator, top: -10 }]}
                    >
                      *
                    </Typography>
                  )}
                </View>
                {info && (
                  <Typography
                    variant="MD"
                    color={colors.info}
                    numberOfLines={1}
                  >
                    {info}
                  </Typography>
                )}
              </View>
              {desc && (
                <Typography
                  variant="SM"
                  color={colors.desc}
                  className={cn('min-w-0 shrink', leadingNone)}
                >
                  {desc}
                </Typography>
              )}
            </View>
          </View>
          {actionComponent && (
            <View className="shrink-0">{actionComponent}</View>
          )}
        </View>
        {content}
      </View>
    </Pressable>
  )
}
